import React, { useState, useCallback } from 'react';
import { EmailThreadList } from './EmailThreadList';
import { EmailThreadView } from './EmailThreadView';
import { EmailCompositionDialog } from './EmailCompositionDialog';
import { EmailAccountManager } from './EmailAccountManager';
import { UnlinkedEmailsTab } from './UnlinkedEmailsTab';
import { Mail, Plus, Settings, Filter, Link2 } from 'lucide-react';

interface RetailerCommunicationHubProps {
    tenderId?: string;
    onClose?: () => void;
    lastSyncedAt?: number;
    /** Called after an unlinked email is assigned — parent can refresh stats */
    onEmailAssigned?: () => void;
}

export const RetailerCommunicationHub: React.FC<RetailerCommunicationHubProps> = ({ tenderId, onClose, lastSyncedAt, onEmailAssigned }) => {
    const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
    const [isComposeOpen, setIsComposeOpen] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [filterMode, setFilterMode] = useState<'current' | 'all'>(tenderId ? 'current' : 'all');
    const [activeTab, setActiveTab] = useState<'inbox' | 'unlinked'>('inbox');
    const [unlinkedCount, setUnlinkedCount] = useState(0);

    // Bump this to force EmailThreadList to refresh after an unmatched email is assigned
    const [threadListKey, setThreadListKey] = useState(0);
    const handleUnmatchedAssigned = useCallback(() => {
        setThreadListKey(k => k + 1);
        onEmailAssigned?.();
    }, [onEmailAssigned]);

    React.useEffect(() => {
        if (tenderId) {
            setFilterMode('current');
        } else {
            setFilterMode('all');
        }
    }, [tenderId]);

    return (
        <div className="flex flex-col h-full bg-gray-100 overflow-hidden">
            {/* ── Header ──────────────────────────────────────────────────── */}
            <div className="bg-white border-b px-4 py-3 flex justify-between items-center flex-shrink-0">
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-blue-600" />
                        <h3 className="font-semibold text-gray-800">Retailer Communications</h3>
                    </div>

                    {/* Tab switcher */}
                    {!showSettings && (
                        <div className="flex items-center bg-gray-100 rounded-lg p-0.5 ml-2">
                            <button
                                onClick={() => setActiveTab('inbox')}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all
                                    ${activeTab === 'inbox'
                                        ? 'bg-white text-gray-800 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                <Mail className="w-3.5 h-3.5" />
                                Inbox
                            </button>
                            <button
                                onClick={() => setActiveTab('unlinked')}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all
                                    ${activeTab === 'unlinked'
                                        ? 'bg-white text-gray-800 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                <Link2 className="w-3.5 h-3.5" />
                                Unlinked
                                {unlinkedCount > 0 && (
                                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full leading-none
                                        ${activeTab === 'unlinked'
                                            ? 'bg-amber-100 text-amber-700'
                                            : 'bg-amber-500 text-white'
                                        }`}>
                                        {unlinkedCount}
                                    </span>
                                )}
                            </button>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        className={`p-2 rounded-md transition-colors ${showSettings ? 'bg-gray-100 text-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
                        title="Email Settings"
                    >
                        <Settings className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => setIsComposeOpen(true)}
                        className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 text-sm font-medium"
                    >
                        <Plus className="w-4 h-4" />
                        New Email
                    </button>
                </div>
            </div>

            {/* ── Main Content ─────────────────────────────────────────────── */}
            <div className="flex flex-1 overflow-hidden">
                {showSettings ? (
                    <div className="flex-1 p-6 overflow-y-auto">
                        <EmailAccountManager />
                    </div>
                ) : activeTab === 'unlinked' ? (
                    /* ── Unlinked emails tab ── */
                    <UnlinkedEmailsTab
                        onCountChange={setUnlinkedCount}
                        onAssigned={handleUnmatchedAssigned}
                    />
                ) : (
                    /* ── Inbox tab ── */
                    <>
                        {/* Thread List Sidebar */}
                        <div className="w-[320px] shrink-0 border-r bg-white overflow-y-auto flex flex-col">
                            <div className="p-3 border-b bg-gray-50 space-y-3">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                                        <Filter className="w-3 h-3" /> {tenderId ? 'Inbox Filter' : 'All Emails'}
                                    </h4>
                                    {tenderId && (
                                        <select
                                            className="text-xs border rounded p-1 bg-white focus:ring-1 focus:ring-blue-500 outline-none"
                                            value={filterMode}
                                            onChange={(e) => setFilterMode(e.target.value as 'current' | 'all')}
                                        >
                                            <option value="current">Current Tender Only</option>
                                            <option value="all">All Emails</option>
                                        </select>
                                    )}
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search emails..."
                                    className="w-full border rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                                />
                            </div>
                            <EmailThreadList
                                key={threadListKey}
                                tenderId={filterMode === 'all' ? undefined : tenderId}
                                onSelectThread={setSelectedThreadId}
                                selectedThreadId={selectedThreadId || undefined}
                                refreshTrigger={lastSyncedAt}
                            />
                        </div>

                        {/* Thread View */}
                        <div className="flex-1 min-w-0 bg-gray-50 flex flex-col overflow-hidden">
                            {selectedThreadId ? (
                                <EmailThreadView
                                    threadId={selectedThreadId}
                                    onClose={() => setSelectedThreadId(null)}
                                />
                            ) : (
                                <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8">
                                    <Mail className="w-12 h-12 mb-4 opacity-20" />
                                    <p className="text-lg font-medium opacity-60">Select a conversation</p>
                                    <p className="text-sm opacity-50">or start a new one</p>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            <EmailCompositionDialog
                isOpen={isComposeOpen}
                onClose={() => setIsComposeOpen(false)}
                tenderId={tenderId}
            />
        </div>
    );
};
